import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AppState } from '@api-ui-app/src/app/app.state';
import { BreadcrumbItem } from '@gelato-api-ui/core/breadcrumbs/breadcrumb-item';
import { StoreProductMode } from '../lib/store-product-mode.enum';
import {
  getSelectedStoreId,
  getStoreName as getSelectedStoreName,
} from '@api-ui-app/src/app/ngrx/e-commerce-stores.selector';
import { PageLeaveConfirmationService } from '@gelato-api-ui/sdk/src/lib/page-leave-confirmation/services/page-leave-confirmation.service';
import { getSelectedClientId } from '@api-ui-app/src/app/ngrx/auth.reducer';
import { AuthService } from '@gelato-api-ui/core/auth/auth.service';
import { ECommerceProductWizardBackRouteService } from '@api-ui-app/src/app/e-commerce-stores/services/e-commerce-product-wizard/e-commerce-product-wizard-back-route.service';
import { LocalStorageItemKey } from '@gelato-api-ui/core/local-storage/types/local-storage-item-key.enum';

@Injectable({
  providedIn: 'root',
})
export class StoreProductModeService {
  selectedStoreId$ = this.store.select(getSelectedStoreId);
  selectedClientId$ = this.store.select(getSelectedClientId);
  isGelatoUser$ = this.authService.isGelatoUser();

  constructor(
    private readonly store: Store<AppState>,
    private readonly authService: AuthService,
    private readonly pageLeaveConfirmationService: PageLeaveConfirmationService,
    private readonly eCommerceProductWizardBackRouteService: ECommerceProductWizardBackRouteService,
  ) {}

  validateActivatedRoute(activatedRoute: ActivatedRoute): Observable<boolean> {
    if (!this.isActivated(activatedRoute)) {
      return of(true);
    }

    return combineLatest([this.selectedStoreId$, this.selectedClientId$, this.isGelatoUser$]).pipe(
      // Empty string is the default value which is set
      // until the app is initialized completely
      filter(([selectedStoreId, _, __]): boolean => selectedStoreId !== ''),
      map(([selectedStoreId, selectedClientId, isGelatoUser]) => {
        const mode = this.getMode(activatedRoute);

        if (isGelatoUser) {
          if (!selectedStoreId || !selectedClientId) {
            this.pageLeaveConfirmationService.navigateWithoutConfirmation(
              this.eCommerceProductWizardBackRouteService.get(
                localStorage.getItem(LocalStorageItemKey.selectedClientId),
                localStorage.getItem(LocalStorageItemKey.selectedStoreId),
                mode === StoreProductMode.PRODUCT_TEMPLATE_WIZARD,
                isGelatoUser,
              ),
            );

            return false;
          }

          return true;
        } else {
          if (!selectedStoreId) {
            this.pageLeaveConfirmationService.navigateWithoutConfirmation(
              this.eCommerceProductWizardBackRouteService.get(
                null,
                localStorage.getItem(LocalStorageItemKey.selectedStoreId),
                mode === StoreProductMode.PRODUCT_TEMPLATE_WIZARD,
                isGelatoUser,
              ),
            );

            return false;
          }

          return true;
        }
      }),
    );
  }

  isActivated(activatedRoute: ActivatedRoute): boolean {
    return Boolean(this.getMode(activatedRoute));
  }

  getMode(activatedRoute: ActivatedRoute): StoreProductMode {
    return activatedRoute?.snapshot?.data?.storeProductMode;
  }

  getRootPath(activatedRoute: ActivatedRoute): string {
    const mode = this.getMode(activatedRoute);

    switch (mode) {
      case StoreProductMode.PRODUCT_WIZARD:
        return '/catalogue/stores/current/products/wizard';
      case StoreProductMode.PRODUCT_TEMPLATE_WIZARD:
        return '/catalogue/stores/current/products/template-wizard';
      case StoreProductMode.PRODUCT_WIZARD_BULK_CONNECT:
        return '/catalogue/stores/current/products/wizard-bulk-connect';
      case StoreProductMode.CONNECT_VARIANT:
        return '/catalogue/stores/current/connect-variant';
      default:
        return '/catalogue';
    }
  }

  getCommonBreadcrumbItems(activatedRoute: ActivatedRoute): Observable<BreadcrumbItem[]> {
    if (!this.isActivated(activatedRoute)) {
      return of([]);
    }

    return combineLatest([this.selectedStoreId$, this.store.select(getSelectedStoreName), this.selectedClientId$]).pipe(
      map(([selectedStoreId, selectedStoreName, selectedClientId]) => {
        if (!selectedStoreId) {
          return [];
        }

        const storesListRoute = selectedClientId ? ['/stores/list', { clientId: selectedClientId }] : ['/stores/list'];

        return [
          {
            translationKey: 'txt_stores_list_page_title',
            routerLink: storesListRoute,
          },
          {
            text: selectedStoreName || '...',
            routerLink: [`/stores/${selectedStoreId}/products/list`],
          },
        ];
      }),
    );
  }
}
